/**
 * Summer Deals Header — Interactive gallery + countdown timer.
 * Cloned from `hybrid-lp-header-v2.js` (gallery + thumbs + touch swipe).
 * Adds an `initCountdown()` function that ticks down to the date
 * provided via `data-sdh-end` on `[data-sdh-countdown]`.
 *
 * Supports multiple section instances on one page.
 */
(function () {
  'use strict';

  function initSection(section) {
    var track = section.querySelector('[data-sdh-track]');
    var slides = section.querySelectorAll('[data-sdh-slide]');
    var thumbs = section.querySelectorAll('[data-sdh-thumb]');
    var thumbsContainer = section.querySelector('.sdh__thumbs');
    var dots = section.querySelectorAll('[data-sdh-dot]');
    var prevBtn = section.querySelector('[data-sdh-prev]');
    var nextBtn = section.querySelector('[data-sdh-next]');
    var main = section.querySelector('[data-sdh-main]');
    var total = slides.length;
    var current = 0;

    if (total === 0 || !track) return;

    // Anchor the thumbnail strip at the first thumb on init so the active
    // first slide and its matching thumb are always aligned. Some mobile
    // browsers otherwise restore a non-zero scroll from a previous session.
    if (thumbsContainer) {
      thumbsContainer.scrollLeft = 0;
      thumbsContainer.scrollTop = 0;
    }

    function goTo(index) {
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;
      current = index;
      track.style.transform = 'translateX(-' + (current * 100) + '%)';

      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === current);
      });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === current);
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === current);
      });

      var activeThumb = thumbs[current];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.getAttribute('data-sdh-thumb'), 10));
      });
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.getAttribute('data-sdh-dot'), 10));
      });
    });

    if (main) {
      var startX = 0;
      main.addEventListener('touchstart', function (e) {
        startX = e.changedTouches[0].screenX;
      }, { passive: true });
      main.addEventListener('touchend', function (e) {
        var diff = startX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) goTo(current + 1);
          else goTo(current - 1);
        }
      }, { passive: true });
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function initCountdown(section) {
    var countdown = section.querySelector('[data-sdh-countdown]');
    if (!countdown) return;

    var endRaw = countdown.getAttribute('data-sdh-end');
    if (!endRaw) return;
    var endTime = Date.parse(endRaw);
    if (isNaN(endTime)) return;

    // Idempotent: if a previous interval was attached (e.g. section reload
    // from the theme editor), clear it before starting a fresh one.
    if (countdown._sdhInterval) {
      clearInterval(countdown._sdhInterval);
    }

    var daysEl = countdown.querySelector('[data-sdh-unit="days"]');
    var hoursEl = countdown.querySelector('[data-sdh-unit="hours"]');
    var minutesEl = countdown.querySelector('[data-sdh-unit="minutes"]');
    var secondsEl = countdown.querySelector('[data-sdh-unit="seconds"]');

    function tick() {
      var diff = endTime - Date.now();
      if (diff < 0) diff = 0;
      var totalSeconds = Math.floor(diff / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
    }

    tick();
    countdown._sdhInterval = setInterval(tick, 1000);
  }

  function initCodeCopy(section) {
    var codeBtn = section.querySelector('[data-sdh-code]');
    if (!codeBtn) return;
    if (codeBtn._sdhCopyBound) return;
    codeBtn._sdhCopyBound = true;

    codeBtn.addEventListener('click', function () {
      var value = codeBtn.getAttribute('data-sdh-code-value') || '';
      if (!value) return;

      var done = function () {
        codeBtn.classList.add('is-copied');
        clearTimeout(codeBtn._sdhCopyTimer);
        codeBtn._sdhCopyTimer = setTimeout(function () {
          codeBtn.classList.remove('is-copied');
        }, 1500);
      };

      // Prefer the async Clipboard API (HTTPS / supported browsers)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(function () {
          fallbackCopy(value);
          done();
        });
      } else {
        fallbackCopy(value);
        done();
      }
    });
  }

  function fallbackCopy(text) {
    // Legacy fallback for browsers without the async Clipboard API.
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.left = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  function init(section) {
    initSection(section);
    initCountdown(section);
    initCodeCopy(section);
  }

  function initAll() {
    document.querySelectorAll('.sdh').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify theme editor adds/reloads the section
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.sdh');
    if (section) init(section);
  });
})();
